import { type LinearSubstep, type SubstepTile, type TileGroup } from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import LinearTile from "~/features/configurator/Linear/components/LinearTile";
import { defaultMemoize } from "reselect";
import { type CustomDispatch } from "~/app/store/types";
import { Col, Row } from "~/common/components/Layout";
import { updateCsticValuesFromTile } from "~/features/configurator/Linear/redux/actions";
import { getKmats } from "~/features/configurator/Linear/redux/selectors";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { formatCsticsToSet } from "~/features/configurator/Linear/utils";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { useTranslation } from "react-i18next";
import { isTileEnabled, isTileStandard } from "~/features/configurator/common/tileUtils";
import { Alert } from "~/common/mui/Alert/Alert";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const ClosureSubstep = ({ def, ...props }: Props) => {
  const { t } = useTranslation();
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const grouppedTiles = getTileData(def, dispatch);
  const kmats = useSelector(getKmats);

  const colSize = (Math.floor(12 / def.tilesPerRow) || 1) as 12 | 11 | 10 | 9 | 8 | 7 | 6 | 5 | 4 | 3 | 2 | 1;

  const errMessage = t("linear.step.select.option");
  const isNoClosingPlugsError = isFakeNoClosingPLugsTileSelected(def) && def.isInvalid;
  const isRegularError = def.isInvalid && !isNoClosingPlugsError;
  const debugLanguage = isDebugLanguageEnabled();

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={def.selectedTile !== null}
      showError={isRegularError}
      errorMessage={def.isInvalid ? t("linear.step.select.option") : ""}
      {...props}
    >
      <div className={"w-100 pb-30"}>
        {grouppedTiles.map((group, groupIndex) => (
          <div className={"pt-30" + (groupIndex !== grouppedTiles.length - 1 ? " pb-30" : "")} key={groupIndex}>
            <div className={"mb-3 d-flex flex-align-center"}>
              <span className={"f-bold label-large"}>
                <LanguageAwareExternalContent content={group.name} className={"scha__lc__external-label"} />
              </span>
              <LinearInfo
                info={group.info}
                className={"icon-margin"}
                popupClassName={"scha__lc__external-content--shaft"}
              />
            </div>
            <Row className={"d-flex"}>
              {group.tiles.map((data, index) => (
                <Col size={colSize} className={"mb-30"} key={index}>
                  <LinearTile
                    onClickHandler={data.onClick}
                    imageUrl={data.imageUrl}
                    title={debugLanguage ? formatCsticsToSet(data.cstics) : data.name}
                    description={data.description}
                    info={data.info}
                    isPicked={
                      data.tileIndex === def.selectedTile ||
                      (groupIndex === 0 &&
                        grouppedTiles.length > 1 &&
                        isTileInLastGroupSelected(def.selectedTile, grouppedTiles) &&
                        isLastTileInGroup(index, group))
                    }
                    isDisabled={!isTileEnabled(data, kmats)}
                    isStandard={isTileStandard(data, kmats)}
                  />
                </Col>
              ))}
            </Row>

            {group.description && (
              <Alert severity={"info"}>
                <LanguageAwareExternalContent content={group.description} className={"scha__lc__external-label"} />
              </Alert>
            )}
          </div>
        ))}
        {isNoClosingPlugsError && (
          <Alert className={"mt-30"} severity="error">
            {errMessage}
          </Alert>
        )}
      </div>
    </SubstepFrame>
  );
};
export default ClosureSubstep;

type Tile = SubstepTile & {
  onClick: () => void;
  tileIndex: number;
};

type GroupedTiles = Omit<TileGroup, "tiles"> & {
  tiles: Tile[];
};

const getTileData = defaultMemoize(
  (stepElement: LinearSubstep, dispatch: CustomDispatch<RootState>): GroupedTiles[] => {
    const allTiles: Tile[] = [];
    let i = 0;
    stepElement.groupedTiles.forEach((group) => {
      group.tiles.forEach((tile) => {
        const index = i++;
        allTiles.push({
          ...tile,
          tileIndex: index,
          onClick: () => dispatch(updateCsticValuesFromTile(stepElement.type, index, tile)),
        });
      });
    });

    i = 0;
    return stepElement.groupedTiles.map((group) => ({
      ...group,
      tiles: group.tiles.map(() => allTiles[i++]),
    }));
  },
);

export const isTileInLastGroupSelected = (selectedTileIndex: number | null, groups: GroupedTiles[] | TileGroup[]) => {
  const tileAmounts = groups.map((group) => group.tiles.length);
  const priorGroupAmount = tileAmounts.slice(0, tileAmounts.length - 1).reduce((a, b) => a + b, 0);
  return !!selectedTileIndex && selectedTileIndex >= priorGroupAmount;
};

export const isLastTileInGroup = (tileIndex: number, group: GroupedTiles | TileGroup) => {
  return tileIndex === group.tiles.length - 1;
};

export const isFakeNoClosingPLugsTileSelected = (substep: LinearSubstep) => {
  return substep.selectedTile === substep.groupedTiles[0].tiles.length - 1;
};
